/**
 * Cheeky Frog — hero product rotator
 *
 * The hero can show real listings instead of one fixed lifestyle shot. Liquid
 * has no shuffle and the storefront is served from a page cache, so the section
 * renders a fixed pool of candidates and the ordering happens here — which is
 * what makes the sequence genuinely per-visit rather than per-cache-entry.
 *
 * Only the first slide ships with its image attached. The rest arrive with
 * their candidates parked in data- attributes, and this file attaches them just
 * before they are needed. That is deliberate: a rotator of eight products was
 * downloading eight full-width images on first paint so that seven of them
 * could sit at opacity 0, which is most of the weight of the page on a phone.
 *
 * The consequence is that the *first frame* is the same for every visitor — it
 * is the one the HTML preloaded, and handing that job to a shuffle would mean
 * waiting for this script before the largest image on the page could even start
 * downloading. The shuffle therefore orders everything *after* the first frame.
 *
 * Progressive enhancement: the markup ships with the first slide already
 * visible and the rest stacked behind it. If this never runs, the hero is a
 * normal product image with a working link.
 */
(function () {
  'use strict';

  var HOLD_MS = 4200;
  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Fisher-Yates. Array.sort with a random comparator is not a shuffle -- it is
     biased, and the bias is worst at the ends, which is exactly where the first
     slide comes from. */
  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  /* Attach a deferred slide's real candidates. Safe to call repeatedly: the
     data- attributes are removed on the first pass, so later calls no-op. */
  function hydrate(slide) {
    if (!slide) return;
    var img = slide.querySelector('.cf-hero__image');
    if (!img || !img.getAttribute('data-cf-hero-srcset')) return;
    img.setAttribute('sizes', img.getAttribute('data-cf-hero-sizes') || '100vw');
    img.setAttribute('srcset', img.getAttribute('data-cf-hero-srcset'));
    img.removeAttribute('data-cf-hero-srcset');
    img.removeAttribute('data-cf-hero-sizes');
    /* It is about to be shown, so it is no longer a low-priority image and no
       longer wants deferring -- lazy would not defer it anyway, since every
       slide sits inside the viewport. */
    img.removeAttribute('fetchpriority');
    img.setAttribute('loading', 'eager');
  }

  /* Fetch ahead of the cross-fade rather than during it, so a slide is decoded
     by the time it is faded up -- but strictly after the page has finished
     loading, and then only in idle time. The first frame's image is the page's
     LCP; a slide nobody has seen yet must never compete with it for bandwidth.
     There are 4.2 seconds before the first cross-fade, which is ample. */
  function prefetchLater(fn) {
    function idle() {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 600);
    }
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  function show(slides, i) {
    slides.forEach(function (s, n) {
      var on = n === i;
      s.classList.toggle('is-on', on);
      if (on) {
        s.removeAttribute('aria-hidden');
        s.removeAttribute('tabindex');
      } else {
        s.setAttribute('aria-hidden', 'true');
        s.setAttribute('tabindex', '-1');
      }
    });
  }

  function start(root) {
    var slides = Array.prototype.slice.call(
      root.querySelectorAll('.cf-hero__slide'));
    if (slides.length < 2) return;

    /* The inline script in the section has already picked the opening slide at
       random and fetched its image. Keep that one in front -- re-picking here
       would either show a second image or blank the one already on screen --
       and shuffle everything after it. */
    var opening = 0;
    for (var n = 0; n < slides.length; n++) {
      if (slides[n].classList.contains('is-on')) { opening = n; break; }
    }
    var first = slides[opening];
    slides = [first].concat(shuffle(slides.filter(function (s) { return s !== first; })));
    slides.forEach(function (s) { root.appendChild(s); });

    var i = 0;
    show(slides, 0);

    if (reduceMotion) return;     // shuffled once, then left alone

    /* Whoever is next needs to be ready before the first cross-fade. */
    prefetchLater(function () { hydrate(slides[1]); });

    var timer = null;
    function tick() {
      i = (i + 1) % slides.length;
      hydrate(slides[i]);                      // in case idle time never came
      show(slides, i);
      hydrate(slides[(i + 1) % slides.length]); // one ahead
    }
    function run() { if (!timer) timer = setInterval(tick, HOLD_MS); }
    function stop() { clearInterval(timer); timer = null; }

    // Nothing animates in a background tab -- it burns battery to redraw a
    // hero nobody is looking at, and the slide it lands on is arbitrary.
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : run();
    });
    // Hovering means someone is reading this one. Let them.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', run);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', run);
    run();
  }

  function init() {
    document.querySelectorAll('[data-cf-hero-rotator]').forEach(start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
